// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2023 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import CreateNewGroupModal from "@bg/App/Components/MyListDetails/CreateNewGroupModal";
import { CustomAppState } from "@bg/App/Store/Reducers";
import { printItems } from "@bg/App/Utilities/PrintItemsQrCode";
import { myListsDetailsOptionsStyles } from "@bg/Overrides/Widgets/MyListsDetails/MyListDetailsOptionsStyles";
import { ProductInfo } from "@insite/client-framework/Common/ProductInfo";
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import Zone from "@insite/client-framework/Components/Zone";
import { makeHandlerChainAwaitable } from "@insite/client-framework/HandlerCreator";
import siteMessage from "@insite/client-framework/SiteMessage";
import { UploadError } from "@insite/client-framework/Store/Components/OrderUpload/Handlers/BatchLoadProducts";
import { getSettingsCollection } from "@insite/client-framework/Store/Context/ContextSelectors";
import { getWishListLinesDataView } from "@insite/client-framework/Store/Data/WishListLines/WishListLinesSelectors";
import addToWishList, {
    AddToWishListParameter,
} from "@insite/client-framework/Store/Data/WishLists/Handlers/AddToWishList";
import { getWishListState } from "@insite/client-framework/Store/Data/WishLists/WishListsSelectors";
import loadWishListIfNeeded from "@insite/client-framework/Store/Pages/MyListDetails/Handlers/LoadWishListIfNeeded";
import loadWishListLines from "@insite/client-framework/Store/Pages/MyListDetails/Handlers/LoadWishListLines";
import setAllWishListLinesIsSelected from "@insite/client-framework/Store/Pages/MyListDetails/Handlers/SetAllWishListLinesIsSelected";
import setEditingSortOrder from "@insite/client-framework/Store/Pages/MyListDetails/Handlers/SetEditingSortOrder";
import updateLoadWishListLinesParameter from "@insite/client-framework/Store/Pages/MyListDetails/Handlers/UpdateLoadWishListLinesParameter";
import translate from "@insite/client-framework/Translate";
import { ProductDto, WishListModel } from "@insite/client-framework/Types/ApiModels";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import OrderUpload from "@insite/content-library/Components/OrderUpload";
import OrderUploadErrorsModal from "@insite/content-library/Components/OrderUploadErrorsModal";
import ProductSelector from "@insite/content-library/Components/ProductSelector";
import Checkbox, { CheckboxProps } from "@insite/mobius/Checkbox";
import Clickable from "@insite/mobius/Clickable";
import Hidden from "@insite/mobius/Hidden";
import Icon from "@insite/mobius/Icon";
import Link from "@insite/mobius/Link";
import LoadingSpinner from "@insite/mobius/LoadingSpinner";
import Modal from "@insite/mobius/Modal";
import OverflowMenu from "@insite/mobius/OverflowMenu";
import Select from "@insite/mobius/Select";
import TextField from "@insite/mobius/TextField";
import debounce from "lodash/debounce";
import uniq from "lodash/uniq";
import React, { ChangeEvent, useCallback, useEffect, useState } from "react";
import { QRCode } from "react-qrcode-logo";
import { connect, ResolveThunks, useDispatch } from "react-redux";
import { css } from "styled-components";

const mapStateToProps = (state: CustomAppState) => ({
    wishListSettings: getSettingsCollection(state).wishListSettings,
    wishListDataView: getWishListState(state, state.pages.myListDetails.wishListId),
    wishListLinesDataView: getWishListLinesDataView(state, state.pages.myListDetails.loadWishListLinesParameter),
    loadWishListLinesParameter: state.pages.myListDetails.loadWishListLinesParameter,
    editingSortOrder: state.pages.myListDetails.editingSortOrder,
    productInfosByWishListLineId: state.pages.myListDetails.productInfosByWishListLineId,
    searchQuery: state.pages.myListDetails.loadWishListLinesParameter.query,
    sortBy: state.pages.myListDetails.loadWishListLinesParameter.sort,
    selectedGroupLines: state.commonActions["selectedGroupLines"],
    uncategorizedGroupName: (getSettingsCollection(state).customSettings as any).unCategorizedGroupName as string,
});

const mapDispatchToProps = {
    updateLoadWishListLinesParameter,
    loadWishListIfNeeded,
    loadWishListLines,
    setAllWishListLinesIsSelected,
    addToWishList: makeHandlerChainAwaitable<AddToWishListParameter, WishListModel>(addToWishList),
    setEditingSortOrder,
};

type Props = WidgetProps & ReturnType<typeof mapStateToProps> & ResolveThunks<typeof mapDispatchToProps>;

export const styles = myListsDetailsOptionsStyles;

const MyListsDetailsOptions = ({
    id,
    wishListSettings,
    wishListDataView,
    wishListLinesDataView,
    loadWishListLinesParameter,
    updateLoadWishListLinesParameter,
    loadWishListIfNeeded,
    loadWishListLines,
    setAllWishListLinesIsSelected,
    addToWishList,
    productInfosByWishListLineId,
    searchQuery,
    sortBy,
    selectedGroupLines,
    uncategorizedGroupName,
}: Props) => {
    const [query, setQuery] = useState(loadWishListLinesParameter.query || "");
    const [addItemsToListIsOpen, setAddItemsToListIsOpen] = useState(false);
    const [uploadItemsModalIsOpen, setUploadItemsModalIsOpen] = useState(false);
    const [groupingModalIsOpen, setGroupingModalIsOpen] = useState(false);
    const [productInfo, setProductInfo] = useState<ProductInfo>({} as ProductInfo);
    const [creatingNewGroup, setIsCreatingNewGroup] = useState(false);
    const [groups, setGroups] = useState({} as any);
    const [listGroups, setListGroups] = useState([]);
    const [isSorting, setIsSorting] = useState(false);

    const dispatch = useDispatch();

    useEffect(() => {
        setQuery("");
        setEditingSortOrder({ editingSortOrder: false });
    }, []);

    const groupData = (lineGroup: any, lineId: string) => {
        const newGroup = groups;
        // eslint-disable-next-line array-callback-return
        lineGroup.map((group: any) => {
            if (newGroup[group.groupName]) {
                if (newGroup[group.groupName]?.indexOf(lineId) === -1) {
                    newGroup[group.groupName] = [...newGroup[group.groupName], lineId];
                }
            } else {
                newGroup[group.groupName] = [];
                newGroup[group.groupName].push(lineId);
            }
        });
        setGroups(newGroup);
    };

    useEffect(() => {
        setGroups([]);
        if (wishListLinesDataView.value && wishListLinesDataView.value.length) {
            wishListLinesDataView.value.forEach(
                l => l?.properties?.grouping && groupData(JSON.parse(l.properties.grouping), l.id),
            );
        }
        if (wishListDataView.value && wishListDataView.value.properties?.groups !== "[]") {
            setListGroups(uniq(JSON.parse(wishListDataView.value.properties?.groups || "[]")));
        }
    }, [wishListDataView, wishListLinesDataView, sortBy, searchQuery, selectedGroupLines]);

    const modalCloseHandler = () => {
        setProductInfo({} as ProductInfo);
        setGroupingModalIsOpen(false);
        setIsCreatingNewGroup(false);
    };

    const callAfterSuccess = (selectedGroup: string) => {
        dispatch({
            type: "Pages/xProductListDetails/TemporarilySaveEmptyList",
            emptyList: selectedGroup,
        });
    };

    const debouncedSearch = useCallback(
        debounce((query: string) => {
            if (!wishListDataView.value) {
                return;
            }
            const searchText = query.trim();
            updateLoadWishListLinesParameter({ query: searchText });
            loadWishListLines();
        }, 200),
        [wishListDataView],
    );

    if (!wishListDataView.value || !wishListSettings) {
        return null;
    }

    const wishList = wishListDataView.value;

    const searchChangeHandler = (event: ChangeEvent<HTMLInputElement>) => {
        const newQuery = event.target.value || "";
        setQuery(newQuery);
        debouncedSearch(newQuery);
    };

    const getIsAllInSelectedGroup = () => {
        if (!selectedGroupLines) {
            return false;
        }
        const isEqualTo: boolean[] = [];
        Object.keys(groups).forEach(group => {
            if (
                groups[group].length > 0 &&
                selectedGroupLines &&
                selectedGroupLines?.hasOwnProperty(group) &&
                groups[group].length === selectedGroupLines[group as any].length
            ) {
                isEqualTo.push(true);
            } else if (groups[group].length > 0) {
                isEqualTo.push(false);
            }
        });

        return isEqualTo.every(i => i);
    };

    const isAllSelected = getIsAllInSelectedGroup();

    const selectAllChangeHandler: CheckboxProps["onChange"] = (_, value) => {
        const allWishListLineIds = value ? wishListLinesDataView?.value?.map(o => o.id) : undefined;
        const emptyGroups = [] as any;
        if (groups && listGroups && !value) {
            listGroups.forEach(group => {
                emptyGroups[group] = [];
            });
        }
        setAllWishListLinesIsSelected({
            isSelected: value,
            wishListLineIds: allWishListLineIds,
            groups: value ? groups : emptyGroups,
        } as any);
    };

    const addItemsToListClickHandler = () => {
        setAddItemsToListIsOpen(true);
    };

    const selectProductHandler = (productInfo: ProductInfo) => {
        setGroupingModalIsOpen(true);
        setProductInfo(productInfo);
    };

    const createNewGroup = () => {
        setGroupingModalIsOpen(true);
        setIsCreatingNewGroup(true);
    };

    const uploadItemsClickHandler = () => {
        setUploadItemsModalIsOpen(true);
    };

    const uploadItemsModalCloseHandler = () => {
        setUploadItemsModalIsOpen(false);
    };

    const uploadProductsHandler = async (products: ProductDto[]) => {
        const productInfos = products.map(o => ({
            productId: o.id,
            qtyOrdered: o.qtyOrdered,
            unitOfMeasure: o.selectedUnitOfMeasure ?? "",
        }));
        const newProductInfo = [] as any;
        const uncategorizedLength = groups[uncategorizedGroupName.toLowerCase()]?.length || 0;

        productInfos.forEach((o, index) => {
            const newInfo = {
                ...o,
                properties: {
                    isUpdateGroup: true,
                    grouping: JSON.stringify({
                        wishListLineGroups: [
                            {
                                groupName: uncategorizedGroupName.toLowerCase(),
                                originalGroupName: "",
                                notes: "",
                                qtyOrdered: o.qtyOrdered,
                                sortOrder: uncategorizedLength + index + 1,
                            },
                        ],
                    }),
                },
            };

            newProductInfo.push(newInfo);
        });
        await addToWishList({
            productInfos: newProductInfo,
            selectedWishList: wishList,
            groups: listGroups as string[],
        } as any);
        setUploadItemsModalIsOpen(false);
        // XNG-Changes
        loadWishListIfNeeded({ wishListId: wishList.id, isWebRequest: true } as any);
    };

    const handleSelectSortOptionChange = (event: React.FormEvent<HTMLSelectElement>) => {
        // unfortunately, we have to force load the wishlist lines in order to re-render
        // the lines with the new sortBy option and the Drag&Drop library work
        dispatch({
            type: "Data/WishListLines/Reset",
        });
        setIsSorting(true);
        updateLoadWishListLinesParameter({ sort: event.currentTarget.value });
        loadWishListLines({
            onSuccess: () => {
                setIsSorting(false);
            },
        });
    };

    // print line qr code
    const printQrCodes = () => {
        if (wishListLinesDataView && wishListLinesDataView.value) {
            printItems(`.canvas-wrapper canvas`, wishListLinesDataView.value, productInfosByWishListLineId as any);
        }
    };

    const canEditWishList = (wishList.allowEdit || !wishList.isSharedList) && !wishList.isAutogenerated;

    return (
        <StyledWrapper {...styles.wrapper}>
            {!addItemsToListIsOpen && (
                <StyledWrapper {...styles.container}>
                    <StyledWrapper {...styles.searchGridItem}>
                        <TextField
                            {...styles.search}
                            placeholder={translate("Search products in list")}
                            defaultValue={query}
                            onChange={searchChangeHandler}
                            label={translate("Search")}
                            data-test-selector="wishListSearch"
                        />
                    </StyledWrapper>
                    <StyledWrapper {...styles.selectAllGridItem}>
                        <Checkbox
                            checked={isAllSelected}
                            onChange={selectAllChangeHandler}
                            data-test-selector="selectAllItems"
                            disabled={wishListLinesDataView.value?.length === 0}
                        >
                            {translate("Select All Products")}
                        </Checkbox>
                    </StyledWrapper>
                    {canEditWishList && (
                        <Hidden
                            below="lg"
                            css={css`
                                ${styles.addItemsGridItem?.css}
                            `}
                        >
                            <Link
                                {...styles.addItemsLink}
                                onClick={addItemsToListClickHandler}
                                data-test-selector="myListDetails_addItemsToList"
                            >
                                {translate("Add Items To List")}
                            </Link>
                        </Hidden>
                    )}
                    <StyledWrapper
                        {...styles.sortOptionWrapper}
                        css={css`
                            ${styles.sortOptionWrapper?.css};
                            display: flex;
                            align-items: center;
                        `}
                    >
                        <Select
                            label={translate("Sort by")}
                            {...styles.selectSortOption}
                            value={loadWishListLinesParameter.sort}
                            onChange={handleSelectSortOptionChange}
                            data-test-selector="myListDetails_SortByOption"
                        >
                            {wishListLinesDataView.value &&
                                wishListLinesDataView.pagination?.sortOptions?.map(sortOption => (
                                    <option value={sortOption.sortType} key={sortOption.sortType}>
                                        {sortOption.sortType === "SortOrder"
                                            ? translate("Choose Sort Type")
                                            : sortOption.displayName}
                                    </option>
                                ))}
                        </Select>
                        {isSorting && (
                            <LoadingSpinner
                                css={css`
                                    margin-left: 8px;
                                `}
                                size={26}
                                color="secondary"
                            />
                        )}
                    </StyledWrapper>

                    <Hidden
                        below="lg"
                        css={css`
                            ${styles.addItemsGridItem?.css}
                        `}
                    >
                        <Link
                            {...styles.printAllQrCodes}
                            // eslint-disable-next-line no-console
                            onClick={printQrCodes}
                            data-test-selector="printAllQrCodes"
                        >
                            {translate("Print All QR Codes")}
                        </Link>
                    </Hidden>

                    {canEditWishList && (
                        <Hidden
                            below="lg"
                            css={css`
                                ${styles.createNewGroupWrapper?.css}
                            `}
                        >
                            <Link
                                {...styles.createNewGroup}
                                onClick={createNewGroup}
                                data-test-selector="printAllQrCodes"
                            >
                                {translate("Create a New Group")}
                            </Link>
                        </Hidden>
                    )}

                    <Hidden above="md" {...styles.actionsPopup}>
                        <OverflowMenu>
                            {canEditWishList && (
                                <Clickable onClick={addItemsToListClickHandler} data-test-selector="addItems">
                                    {translate("Add Items To List")}
                                </Clickable>
                            )}
                            <Clickable onClick={printQrCodes} data-test-selector="printAllQrCodes">
                                {translate("Print All QR Codes")}
                            </Clickable>
                            {canEditWishList && (
                                <Clickable onClick={createNewGroup} data-test-selector="printAllQrCodes">
                                    {translate("Create a New Group")}
                                </Clickable>
                            )}
                        </OverflowMenu>
                    </Hidden>
                </StyledWrapper>
            )}
            {addItemsToListIsOpen && (
                <StyledWrapper {...styles.addItemsGridContainer}>
                    <StyledWrapper {...styles.productSelectorGridItem}>
                        <ProductSelector
                            selectButtonTitle={translate("Add to List")}
                            skipInventoryValidation={true}
                            denyFixedConfiguration={true}
                            onSelectProduct={selectProductHandler}
                            productIsConfigurableMessage={siteMessage("ListUpload_CannotOrderConfigurable")}
                            productIsUnavailableMessage={siteMessage("Product_NotFound")}
                            extendedStyles={styles.productSelectorStyles}
                        />
                        <Link
                            {...styles.uploadItemsLink}
                            onClick={uploadItemsClickHandler}
                            data-test-selector="myListDetails_uploadItems"
                        >
                            {translate("Upload Items")}
                        </Link>
                        <Icon
                            {...styles.closeAddItemsToList}
                            onClick={() => {
                                setAddItemsToListIsOpen(false);
                            }}
                        />
                    </StyledWrapper>
                    <StyledWrapper {...styles.uploadItemsGridItem}>
                        <Modal
                            {...styles.uploadItemsModal}
                            headline={translate("Upload Items To List")}
                            isOpen={uploadItemsModalIsOpen}
                            handleClose={uploadItemsModalCloseHandler}
                            data-test-selector="myListDetails_uploadItemsToListModal"
                        >
                            <StyledWrapper>
                                <StyledWrapper>
                                    <OrderUpload
                                        descriptionText={siteMessage("Lists_UploadLinkTooltip")}
                                        checkInventory={false}
                                        templateUrl="/Excel/ListUploadTemplate.xlsx"
                                        onUploadProducts={uploadProductsHandler}
                                        extendedStyles={styles.orderUploadStyles}
                                    />
                                    <OrderUploadErrorsModal
                                        descriptionText={siteMessage("ListUpload_UploadError")}
                                        uploadErrorText={siteMessage("ListUpload_UploadError")}
                                        rowsLimitExceededText={siteMessage("ListUpload_RowsLimitExceeded")}
                                        errorReasons={{
                                            [UploadError.ConfigurableProduct]: siteMessage(
                                                "ListUpload_CannotOrderConfigurable",
                                            ),
                                            [UploadError.StyledProduct]: siteMessage("ListUpload_CannotOrderStyled"),
                                            [UploadError.InvalidUnit]: translate("Invalid U/M"),
                                            [UploadError.NotFound]: siteMessage("Product_NotFound"),
                                        }}
                                        extendedStyles={styles.orderUploadErrorsModalStyles}
                                    />
                                </StyledWrapper>
                                <StyledWrapper>
                                    <Zone zoneName="UploadInstructions" contentId={id} />
                                </StyledWrapper>
                            </StyledWrapper>
                        </Modal>
                    </StyledWrapper>
                </StyledWrapper>
            )}
            <CreateNewGroupModal
                creatingNewGroup={creatingNewGroup}
                productInfo={productInfo}
                groupingModalIsOpen={groupingModalIsOpen}
                closeModalCallback={modalCloseHandler}
                callAfterSuccess={callAfterSuccess}
            />
            <StyledWrapper
                css={css`
                    display: none;
                `}
                className="canvas-wrapper"
            >
                {wishListLinesDataView?.value?.map(l => (
                    <QRCode
                        key={`qrCode_${l.id}`}
                        value={`${window.location.origin}${l.productUri}`}
                        logoWidth={150}
                        logoHeight={150}
                    />
                ))}
            </StyledWrapper>
        </StyledWrapper>
    );
};

const widgetModule: WidgetModule = {
    component: connect(mapStateToProps, mapDispatchToProps)(MyListsDetailsOptions),
    definition: {
        group: "My Lists Details",
        displayName: "Options",
        allowedContexts: ["MyListsDetailsPage"],
    },
};

export default widgetModule;
